/*****************************************************************
 *  ZOHO BRIDGE PANEL — TALKING TO THE SERVER                      *
 *  File: apps/web/src/pages/bridge/bridgeApi.js                   *
 *  What it does: every call the panel makes to                    *
 *  /api/bridge/zoho, in one place. Uses the suite's shared        *
 *  authFetch (cookie login + one silent refresh).                 *
 *  Every function answers { ok, status, data, message } and       *
 *  never throws, so a screen can always say what happened.        *
 *  Reads: the bridge API.   Writes: the bridge API (decisions,    *
 *  keys, relinks) — only when a person pressed a button.          *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 — THE ONE CALLER                                     *
 *  SECTION 2 — THE NAMED CALLS                                    *
 *****************************************************************/

import { API, authFetch } from '../../lib/api';

// Where the bridge door lives under the API.
const BRIDGE_PATH = '/bridge/zoho';

/*****************************************************************
 *  SECTION 1 — THE ONE CALLER                                     *
 *****************************************************************/

/* ---- callBridge(method, subPath, body) -------------------------
   Calls the bridge door and reads the suite's envelope.
   In: GET/POST; the path after /bridge/zoho; an optional body.
   Out: { ok, status, data, message }. Never throws.             */
async function callBridge(method, subPath, body) {
  try {
    const fetchOptions = { method };

    if (body !== undefined) {
      fetchOptions.body = JSON.stringify(body);
    }

    const response = await authFetch(API + BRIDGE_PATH + subPath, fetchOptions);
    let envelope = null;

    try {
      envelope = await response.json();
    } catch (parseError) {
      envelope = null;
    }

    const hasEnvelope = Boolean(envelope) && typeof envelope === 'object';
    const data = hasEnvelope && envelope.data !== undefined ? envelope.data : null;
    let message = '';

    if (hasEnvelope && envelope.error) {
      message = typeof envelope.error === 'string' ? envelope.error : envelope.error.message || '';
    }

    return {
      ok: response.ok,
      status: response.status,
      data,
      message,
    };
  } catch (networkError) {
    return {
      ok: false,
      status: 0,
      data: null,
      message: 'Could not reach the server. Check your connection.',
    };
  }
}

/*****************************************************************
 *  SECTION 2 — THE NAMED CALLS                                    *
 *****************************************************************/

/* ---- the holding pile ---- */
export function fetchSummary() {
  return callBridge('GET', '/summary');
}

export function fetchPile(statusFilter, kindFilter) {
  const queryParts = new URLSearchParams();

  if (statusFilter) {
    queryParts.set('status', statusFilter);
  }

  if (kindFilter) {
    queryParts.set('kind', kindFilter);
  }

  return callBridge('GET', '/inbox?' + queryParts.toString());
}

export function approveRecord(inboxId, why, allowOlder) {
  return callBridge('POST', '/inbox/' + inboxId + '/approve', { why, allowOlder: Boolean(allowOlder) });
}

export function rejectRecord(inboxId, why) {
  return callBridge('POST', '/inbox/' + inboxId + '/reject', { why });
}

export function approveManyRecords(inboxIds, why) {
  return callBridge('POST', '/inbox/approve-many', { ids: inboxIds, why });
}

/* ---- prices ---- */
export function fetchPrices() {
  return callBridge('GET', '/prices');
}

export function fetchPriceHistory(zohoItemId) {
  return callBridge('GET', '/prices/' + encodeURIComponent(zohoItemId) + '/history');
}

/* ---- customer links ---- */
export function fetchLinks(includeOld) {
  return callBridge('GET', '/links' + (includeOld ? '?all=1' : ''));
}

export function relinkCustomer(linkId, customerId, why) {
  return callBridge('POST', '/links/' + linkId + '/relink', { customerId, why });
}

/* ---- Customer Butler's own search (its door, its rules) ---- */
export async function searchButlerCustomers(searchText) {
  try {
    const response = await authFetch(API + '/customer/customers?search=' + encodeURIComponent(searchText));
    const envelope = await response.json();
    const rows = envelope && Array.isArray(envelope.data) ? envelope.data : [];

    return { ok: response.ok, status: response.status, data: rows, message: '' };
  } catch (searchError) {
    return { ok: false, status: 0, data: [], message: 'Could not search customers.' };
  }
}

/* ---- the log ---- */
export function fetchLog() {
  return callBridge('GET', '/log');
}

/* ---- machine keys ---- */
export function fetchKeys() {
  return callBridge('GET', '/keys');
}

export function makeKey(keyName) {
  return callBridge('POST', '/keys', { name: keyName });
}

export function revokeKey(keyId, why) {
  return callBridge('POST', '/keys/' + keyId + '/revoke', { why });
}
